import "@xyflow/react/dist/base.css";
import {
  Background,
  Controls,
  type Edge,
  MarkerType,
  type Node,
  type OnNodeDrag,
  ReactFlow,
  ReactFlowProvider,
  useNodesInitialized,
  useNodesState,
  useReactFlow,
} from "@xyflow/react";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CardAction, type FlowCard } from "../../api/dto/FlowCard";
import { EditCardActionModal } from "../../modals/EditCardActionModal";
import { languageKeysOf } from "../../util/languageKeysOf";
import { Select } from "../form/Select";
import { useModal } from "../layout/ModalProvider";
import { DisplayLanguageContext } from "./CardContentPreview";
import { type CardActionEdgeData, cardFlowEdgeTypes, colorForActionType } from "./CardFlowEdge";
import {
  type AddCardNodeData,
  type CardNodeData,
  type ColumnHeaderNodeData,
  type CompletionNodeData,
  type Connector,
  cardFlowNodeTypes,
  cardNodeWidth,
  type DropZoneNodeData,
  type OffsetStepsNodeData,
} from "./CardFlowNodes";
import type { CardApi } from "./cardApi";

// Wide gap between columns (well beyond the card width itself) so action arrows — which run roughly
// horizontally between columns — have visible room to curve, rather than being squashed edge-to-edge.
const COLUMN_X_PITCH = cardNodeWidth + 220;
const CARD_GAP = 24;
const ESTIMATED_CARD_HEIGHT = 220;
// Headers are 32px tall (h-8), leaving a 16px gap above the first card.
const HEADER_Y = -48;
const DROP_ZONE_ID_PREFIX = "__drop-zone-";
const ADD_CARD_BUTTON_HEIGHT = 48;
const POSITION_LINE_HEIGHT = 4;
// A target with at least this many incoming actions gets a connector: its actions are drawn as stubs
// instead of lines (see Connector in CardFlowNodes).
const CONNECTOR_MIN_ACTIONS = 5;
// Clearly distinct from each other and from the green/amber/red action colors.
const CONNECTOR_COLORS = ["#4f46e5", "#0891b2", "#c026d3", "#7c3aed", "#0f766e", "#475569"];
// The two fixed placeholder cards: the offset steps on the left (whose handle points at the first
// card) and the completion on the right (which every action without a next card points at).
const OFFSET_STEPS_CARD_ID = "offset-steps-card";
const COMPLETION_CARD_ID = "completion-card";

// How far beside a column a dragged card's center still counts as a drop on that column — the rest of
// the gap between two columns inserts a new step there.
const COLUMN_DROP_MARGIN = 40;

type DropTarget =
  | { kind: "column"; column: number; position: number }
  // A new step at `column`: that column and every one after it shift one to the right.
  | { kind: "newColumn"; column: number };

// Shared between the live-drag preview and the actual drop. A card whose center is within a column
// (plus COLUMN_DROP_MARGIN) drops there, at however many of that column's other cards currently sit
// above it. Anywhere else — the rest of a gap, left of the first column or past the last one —
// inserts a new step.
function computeDropTarget(
  dragPosition: { x: number; y: number },
  draggedUuid: string,
  cards: FlowCard[],
  nodes: Node[],
  showCols: number,
): DropTarget {
  const nearest = Math.round(dragPosition.x / COLUMN_X_PITCH);
  const offset = dragPosition.x - nearest * COLUMN_X_PITCH;
  if (
    Math.abs(offset) > cardNodeWidth / 2 + COLUMN_DROP_MARGIN ||
    nearest < 0 ||
    nearest >= showCols
  ) {
    const column = offset > 0 ? nearest + 1 : nearest;
    return { kind: "newColumn", column: Math.max(0, Math.min(showCols, column)) };
  }
  const position = cards.filter(
    (c) =>
      c.column === nearest &&
      c.uuid !== draggedUuid &&
      (nodes.find((n) => n.id === c.uuid)?.position.y ?? Number.POSITIVE_INFINITY) < dragPosition.y,
  ).length;
  return { kind: "column", column: nearest, position };
}

// The cards as they'd be after dropping `dragged` on `target` — not yet compacted (see compactColumns).
// The target column is renumbered 0..n-1 with `dragged` inserted at its position, so positions never
// tie (a tie would leave the dragged card above the card it was dropped below).
function applyDropTarget(
  cards: FlowCard[],
  dragged: FlowCard,
  target: DropTarget,
): FlowCard[] {
  const shifted = cards.map((c) =>
    target.kind === "newColumn" && c.column >= target.column ? { ...c, column: c.column + 1 } : c,
  );
  const columnOrder = shifted
    .filter((c) => c.column === target.column && c.uuid !== dragged.uuid)
    .sort((a, b) => a.positionInColumn - b.positionInColumn)
    .map((c) => c.uuid);
  columnOrder.splice(target.kind === "column" ? target.position : 0, 0, dragged.uuid);
  return shifted.map((c) => {
    const position = columnOrder.indexOf(c.uuid);
    if (position === -1) return c;
    return { ...c, column: target.column, positionInColumn: position };
  });
}

// Where an action leads: its next card, or the completion placeholder when it has none.
function actionTarget(action: CardAction): string {
  return action.nextCardUuid ?? COMPLETION_CARD_ID;
}

// Steps follow the cards: renumbers the columns in use to 0..n-1, so a column left empty (its last
// card moved out or deleted) disappears and every column after it shifts one to the left.
function compactColumns(cards: FlowCard[]): FlowCard[] {
  const used = Array.from(new Set(cards.map((c) => c.column))).sort((a, b) => a - b);
  return cards.map((c) => {
    const column = used.indexOf(c.column);
    return column === c.column ? c : { ...c, column };
  });
}

// The reusable swimlane/flow editor for anything shaped like FlowCard (columns of cards,
// drag-and-drop between/within columns, action arrows between cards) — used as-is (same component,
// different `api` binding) by the checklist and lifting category assistant editor pages. Everything
// domain-specific is injected: `cards` is just the flat
// list, `api` is the small set of functions (see cardApi.ts) that actually talk to a backend. Owns
// its own language selector/context, since "which language am I looking at" is inherent to the
// editor, not something each page should have to re-derive. `stepOffset` is the number of steps that
// precede column 0; they're shown as a single placeholder column in front of the real ones, whose
// handle is dragged onto whichever card is the first. A matching completion placeholder sits after
// the last column, for actions that end the flow. `cardTypeOptions` are the card types the page's
// domain knows, by value, with their label.
export const CardFlowEditor: FC<{
  cards: FlowCard[];
  stepOffset?: number;
  api: CardApi;
  cardTypeOptions: Record<string, string>;
}> = (props) => {
  const languages = useMemo(() => {
    const found = new Set<string>();
    for (const card of props.cards) {
      for (const lang of languageKeysOf(card.name)) found.add(lang);
      for (const block of card.contentBlocks) {
        for (const lang of languageKeysOf(block.content)) found.add(lang);
      }
    }
    return Array.from(found);
  }, [props.cards]);

  const [displayLanguage, setDisplayLanguage] = useState<string>("nl");
  useEffect(() => {
    if (languages.length > 0 && !languages.includes(displayLanguage)) {
      setDisplayLanguage(languages.includes("nl") ? "nl" : languages[0]);
    }
  }, [languages, displayLanguage]);

  return (
    <DisplayLanguageContext.Provider value={displayLanguage}>
      <div className={"flex flex-col h-full"}>
        {languages.length > 0 && (
          <div className={"flex items-center justify-end px-2 py-1 border-b border-slate-200"}>
            <div className={"w-32"}>
              <Select
                label={"Taal"}
                inline
                options={Object.fromEntries(languages.map((l) => [l, l.toUpperCase()]))}
                value={displayLanguage}
                onChange={setDisplayLanguage}
              />
            </div>
          </div>
        )}
        <div className={"flex-1"}>
          <ReactFlowProvider>
            <CardFlowCanvas
              cards={props.cards}
              stepOffset={props.stepOffset ?? 0}
              api={props.api}
              cardTypeOptions={props.cardTypeOptions}
            />
          </ReactFlowProvider>
        </div>
      </div>
    </DisplayLanguageContext.Provider>
  );
};

const CardFlowCanvas: FC<{
  cards: FlowCard[];
  stepOffset: number;
  api: CardApi;
  cardTypeOptions: Record<string, string>;
}> = (props) => {
  const nodesInitialized = useNodesInitialized();
  const { getNodes } = useReactFlow();
  const [measuredHeights, setMeasuredHeights] = useState<Record<string, number>>({});

  // Local, optimistic card state: every mutation below patches this directly from its own API
  // response instead of firing the global refresh() (which re-fetches every shared resource in the
  // app, not just this editor's own data — that's what made every interaction here feel like a full
  // reload). Only re-synced from props.cards when that reference actually changes, i.e. a genuine
  // external cause (initial mount, or someone else's refresh() elsewhere in the app) — never as a
  // side effect of our own saves, since those no longer touch props.cards at all.
  const [cards, setCards] = useState<FlowCard[]>(props.cards);
  useEffect(() => {
    setCards(props.cards);
  }, [props.cards]);

  // useModal()'s modal body is captured once, as a closure, at the moment .open() is called (see
  // ModalProvider.tsx: the pushed Modal object — including its `body` — is never replaced by a later
  // render). Under a full-reload-per-action architecture that staleness window barely matters; since
  // saves here patch local `cards` in place without reloading, a modal left open across another
  // save/drag would silently spread an outdated `column`/`positionInColumn`/etc. back to the server on
  // its own save. cardsRef always points at the latest `cards`, so any save-time lookup that goes
  // through it (see EditCardActionModal/EditCardContentModal's `getFreshCards`) stays correct
  // regardless of how long the modal has been open.
  const cardsRef = useRef(cards);
  useEffect(() => {
    cardsRef.current = cards;
  }, [cards]);
  const getFreshCards = useCallback(() => cardsRef.current, []);

  const patchCard = (updated: FlowCard) => {
    setCards((old) => old.map((c) => (c.uuid === updated.uuid ? updated : c)));
  };
  const patchCards = (updated: FlowCard[]) => {
    const byUuid = Object.fromEntries(updated.map((c) => [c.uuid, c]));
    setCards((old) => old.map((c) => byUuid[c.uuid] ?? c));
  };
  const addCard = (created: FlowCard) => {
    setCards((old) => [...old, created]);
  };

  // Every layout change (drag, move buttons, delete) goes through here: compacts the columns (see
  // compactColumns) and saves only the cards whose column/position actually changed. Compares against
  // cardsRef rather than `cards`, since a delete modal's callback can outlive the render it came from.
  // Returns false, saving nothing, when the layout didn't change.
  const saveLayout = (next: FlowCard[]): boolean => {
    const current = Object.fromEntries(cardsRef.current.map((c) => [c.uuid, c]));
    const changed = compactColumns(next).filter((c) => {
      const old = current[c.uuid];
      return old && (old.column !== c.column || old.positionInColumn !== c.positionInColumn);
    });
    if (changed.length === 0) return false;
    Promise.all(changed.map((c) => props.api.updateCard(c))).then(patchCards);
    return true;
  };

  // The backend ends the actions that led to the deleted card itself; mirror that locally, so a later
  // save of those cards doesn't resend the deleted card as their next card.
  const removeCard = (uuid: string) => {
    const remaining = cardsRef.current
      .filter((c) => c.uuid !== uuid)
      .map((c) =>
        c.actions.some((a) => a.nextCardUuid === uuid)
          ? {
              ...c,
              actions: c.actions.map((a) =>
                a.nextCardUuid === uuid
                  ? new CardAction(a.id, a.answerValue, a.actionType, null)
                  : a,
              ),
            }
          : c,
      );
    setCards(remaining);
    saveLayout(remaining);
  };

  // Two-pass layout: cards render first at an estimated pitch (nothing is truncated per the parity
  // requirement, so real heights vary a lot), then once React Flow has measured every node we correct
  // each column's cumulative y — converges in 1-2 passes since re-measuring unchanged content reports
  // the same heights, and the functional update below is a no-op (no re-render) when nothing changed.
  useEffect(() => {
    if (!nodesInitialized) return;
    const heights: Record<string, number> = {};
    for (const n of getNodes()) {
      if (n.type === "card" && n.measured?.height) {
        heights[n.id] = n.measured.height;
      }
    }
    setMeasuredHeights((old) => {
      const changed = Object.keys(heights).some((id) => heights[id] !== old[id]);
      return changed ? { ...old, ...heights } : old;
    });
  }, [nodesInitialized, getNodes]);

  // Move logic, operating on local cards state and patching it from each response rather than a
  // global refresh(). Moves to the bottom of the neighbouring column; moving left from the first or
  // right from the last column starts a new step there.
  const moveCardToColumn = (card: FlowCard, direction: -1 | 1) => {
    const column = card.column + direction;
    const target: DropTarget =
      column < 0
        ? { kind: "newColumn", column: 0 }
        : { kind: "column", column, position: cards.filter((c) => c.column === column).length };
    saveLayout(applyDropTarget(cards, card, target));
  };

  const moveCardInColumn = (card: FlowCard, direction: -1 | 1) => {
    const index = cards
      .filter((c) => c.column === card.column)
      .sort((a, b) => a.positionInColumn - b.positionInColumn)
      .findIndex((c) => c.uuid === card.uuid);
    const position = index + direction;
    saveLayout(applyDropTarget(cards, card, { kind: "column", column: card.column, position }));
  };

  // The backend unmarks the previous first card itself; mirror that locally instead of re-fetching.
  const setFirstCard = async (card: FlowCard) => {
    if (card.isFirst) return;
    const updated = await props.api.updateCard({ ...card, isFirst: true });
    setCards((old) =>
      old.map((c) =>
        c.uuid === updated.uuid ? updated : c.isFirst ? { ...c, isFirst: false } : c,
      ),
    );
  };

  // Targets with at least CONNECTOR_MIN_ACTIONS incoming actions, lettered left to right so the letters
  // follow the flow (the completion placeholder last).
  const connectors = useMemo(() => {
    const counts = new Map<string, number>();
    for (const action of cards.flatMap((c) => c.actions)) {
      const target = actionTarget(action);
      counts.set(target, (counts.get(target) ?? 0) + 1);
    }
    const targets = [...cards]
      .sort((a, b) => a.column - b.column || a.positionInColumn - b.positionInColumn)
      .map((c) => c.uuid)
      .concat(COMPLETION_CARD_ID)
      .filter((target) => (counts.get(target) ?? 0) >= CONNECTOR_MIN_ACTIONS);
    return new Map(
      targets.map((target, i): [string, Connector] => [
        target,
        {
          letter: String.fromCharCode(65 + (i % 26)),
          color: CONNECTOR_COLORS[i % CONNECTOR_COLORS.length],
          count: counts.get(target) ?? 0,
        },
      ]),
    );
  }, [cards]);

  // Only the columns in use — new steps are made by dropping a card between two of them. An empty
  // checklist still gets one, so there's somewhere to add the first card.
  const showCols = useMemo(() => {
    if (cards.length === 0) return 1;
    return Math.max(...cards.map((c) => c.column)) + 1;
  }, [cards]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: moveCardToColumn/moveCardInColumn/patch*/add/removeCard close over cards/props.api and are recreated every render by design
  const derivedNodes = useMemo<Node[]>(() => {
    const result: Node[] = [];
    // The preceding steps sit one pitch left of column 0 (negative x) and the completion one pitch
    // right of the last column, so none of the column math below needs to know about them —
    // computeDropTarget treats drops there as a new first / last step.
    const offsetStepsData: OffsetStepsNodeData = { stepOffset: props.stepOffset };
    const completionData: CompletionNodeData = {
      incomingConnector: connectors.get(COMPLETION_CARD_ID) ?? null,
    };
    result.push(
      {
        id: "offset-steps-header",
        type: "offsetStepsHeader",
        position: { x: -COLUMN_X_PITCH, y: HEADER_Y },
        data: offsetStepsData as unknown as Record<string, unknown>,
        draggable: false,
        selectable: false,
        connectable: false,
      },
      {
        id: OFFSET_STEPS_CARD_ID,
        type: "offsetStepsCard",
        position: { x: -COLUMN_X_PITCH, y: 0 },
        data: offsetStepsData as unknown as Record<string, unknown>,
        draggable: false,
        selectable: false,
      },
      {
        id: "completion-header",
        type: "completionHeader",
        position: { x: showCols * COLUMN_X_PITCH, y: HEADER_Y },
        data: {},
        draggable: false,
        selectable: false,
        connectable: false,
      },
      {
        id: COMPLETION_CARD_ID,
        type: "completionCard",
        position: { x: showCols * COLUMN_X_PITCH, y: 0 },
        data: completionData as unknown as Record<string, unknown>,
        draggable: false,
        selectable: false,
      },
    );
    for (let col = 0; col < showCols; col++) {
      const colCards = cards
        .filter((c) => c.column === col)
        .sort((a, b) => a.positionInColumn - b.positionInColumn);

      const headerData: ColumnHeaderNodeData = { columnId: col, stepOffset: props.stepOffset };
      result.push({
        id: `col-header-${col}`,
        type: "columnHeader",
        position: { x: col * COLUMN_X_PITCH, y: HEADER_Y },
        data: headerData as unknown as Record<string, unknown>,
        draggable: false,
        selectable: false,
        connectable: false,
      });

      let y = 0;
      colCards.forEach((card, i) => {
        const cardData: CardNodeData = {
          cards,
          card,
          api: props.api,
          cardTypeOptions: props.cardTypeOptions,
          // Moving out of the first/last column starts a new step — a no-op for a card alone in it.
          canMoveLeft: col > 0 || colCards.length > 1,
          canMoveRight: col < showCols - 1 || colCards.length > 1,
          canMoveUp: i > 0,
          canMoveDown: i < colCards.length - 1,
          incomingConnector: connectors.get(card.uuid) ?? null,
          outgoingStubs: card.actions.flatMap((action) => {
            const connector = connectors.get(actionTarget(action));
            return connector ? [{ action, connector }] : [];
          }),
          onEditAction: (actionId) => editActionModal.open([card.uuid, actionId, ""]),
          onMoveColumn: moveCardToColumn,
          onMoveInColumn: moveCardInColumn,
          onCardUpdated: patchCard,
          onCardDeleted: removeCard,
          getFreshCards,
        };
        result.push({
          id: card.uuid,
          type: "card",
          position: { x: col * COLUMN_X_PITCH, y },
          data: cardData as unknown as Record<string, unknown>,
          draggable: true,
        });
        y += (measuredHeights[card.uuid] ?? ESTIMATED_CARD_HEIGHT) + CARD_GAP;
      });

      const addCardData: AddCardNodeData = {
        api: props.api,
        cardTypeOptions: props.cardTypeOptions,
        columnId: col,
        posInColumn: colCards.length,
        onCardCreated: addCard,
      };
      result.push({
        id: `col-add-${col}`,
        type: "addCard",
        position: { x: col * COLUMN_X_PITCH, y },
        data: addCardData as unknown as Record<string, unknown>,
        draggable: false,
        // Deliberately not connectable:false/selectable:false — React Flow appears to disable
        // pointer-events entirely on a node that's neither selectable nor connectable, which silently
        // swallowed clicks on the button inside it.
      });
    }
    return result;
  }, [
    cards,
    connectors,
    props.api,
    props.cardTypeOptions,
    props.stepOffset,
    showCols,
    measuredHeights,
  ]);

  // Locally mutable copy of derivedNodes: React Flow needs onNodesChange-backed state (not a bare
  // useMemo) to animate dragging smoothly. Re-synced from derivedNodes on every data/layout change —
  // this only ever changes between drags (a save patches local `cards` -> new derivedNodes), never
  // mid-gesture, so it doesn't fight with an in-progress drag.
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  useEffect(() => {
    setNodes(derivedNodes);
  }, [derivedNodes, setNodes]);

  const withoutDropZones = (nds: Node[]) =>
    nds.filter((n) => !n.id.startsWith(DROP_ZONE_ID_PREFIX));

  // Live preview while dragging, using the same targeting as the actual drop below (see DropZoneNode):
  // a new-step zone in every gap between columns, the one under the card highlighted, or — over a
  // column — a line where in it the card goes. Purely visual, nothing is saved until the drag
  // stops. Rebuilt every drag tick, so each zone gets an explicit width/height — React Flow hides a
  // node until it has measured it, which made the zones flicker.
  const onNodeDrag: OnNodeDrag = (_event, node) => {
    if (node.type !== "card") return;
    const target = computeDropTarget(node.position, node.id, cards, nodes, showCols);
    // From the step headers down to the lowest add-card button, i.e. the bottom of the tallest column.
    const bottom = Math.max(...nodes.filter((n) => n.type === "addCard").map((n) => n.position.y));
    const dropZone = (
      id: string,
      data: DropZoneNodeData,
      position: { x: number; y: number },
      width: number,
      height: number,
    ): Node => ({
      id: `${DROP_ZONE_ID_PREFIX}${id}`,
      type: "dropZone",
      position,
      width,
      height,
      data: data as unknown as Record<string, unknown>,
      draggable: false,
      selectable: false,
      connectable: false,
      zIndex: -1,
    });
    const zones: Node[] = [];
    for (let col = 0; col <= showCols; col++) {
      zones.push(
        dropZone(
          `newColumn-${col}`,
          { kind: "newColumn", active: target.kind === "newColumn" && target.column === col },
          { x: (col - 1) * COLUMN_X_PITCH + cardNodeWidth + COLUMN_DROP_MARGIN, y: HEADER_Y },
          COLUMN_X_PITCH - cardNodeWidth - 2 * COLUMN_DROP_MARGIN,
          bottom + ADD_CARD_BUTTON_HEIGHT - HEADER_Y,
        ),
      );
    }

    // The insertion line, centered in the gap above the card the dragged one will go before (or above
    // the column's add-card button, at the bottom) — left out when the drop wouldn't change the order.
    const dragged = cards.find((c) => c.uuid === node.id);
    if (target.kind === "column" && dragged) {
      const siblings = cards
        .filter((c) => c.column === target.column && c.uuid !== dragged.uuid)
        .sort((a, b) => a.positionInColumn - b.positionInColumn);
      const currentIndex = siblings.filter(
        (c) => c.positionInColumn < dragged.positionInColumn,
      ).length;
      const nextNodeId = siblings[target.position]?.uuid ?? `col-add-${target.column}`;
      const lineY = (nodes.find((n) => n.id === nextNodeId)?.position.y ?? 0) - CARD_GAP / 2;
      if (target.column !== dragged.column || target.position !== currentIndex) {
        zones.push(
          dropZone(
            "position",
            { kind: "position" },
            { x: target.column * COLUMN_X_PITCH, y: lineY - POSITION_LINE_HEIGHT / 2 },
            cardNodeWidth,
            POSITION_LINE_HEIGHT,
          ),
        );
      }
    }

    setNodes((nds) => [...withoutDropZones(nds), ...zones]);
  };

  // Snap-on-drop: same targeting as the live preview above. When the layout actually changed, the save
  // round-trip patches local `cards`, which recomputes derivedNodes and re-syncs `nodes` automatically
  // (the effect above). When nothing changed (e.g. a same-column drag that didn't cross a sibling
  // boundary), that recompute never happens on its own, so the dragged node would otherwise stay
  // wherever it was physically dropped instead of snapping back to its slot — the explicit
  // setNodes(derivedNodes) below covers exactly that case. Either way the drop zones always get removed
  // here.
  const onNodeDragStop: OnNodeDrag = (_event, node) => {
    setNodes(withoutDropZones);
    if (node.type !== "card") return;
    const card = cards.find((c) => c.uuid === node.id);
    if (!card) {
      setNodes(derivedNodes);
      return;
    }

    const target = computeDropTarget(node.position, card.uuid, cards, nodes, showCols);
    if (!saveLayout(applyDropTarget(cards, card, target))) {
      setNodes(derivedNodes);
    }
  };

  const edges = useMemo<Edge[]>(() => {
    const cardUuids = new Set(cards.map((c) => c.uuid));
    const result: Edge[] = [];
    // The preceding steps lead into whichever card is marked as first — not an action.
    const firstCard = cards.find((c) => c.isFirst);
    if (firstCard) {
      result.push({
        id: `offset-steps:${firstCard.uuid}`,
        source: OFFSET_STEPS_CARD_ID,
        sourceHandle: "out",
        target: firstCard.uuid,
        targetHandle: "in",
        selectable: false,
        style: { stroke: "#cbd5e1", strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: "#cbd5e1" },
      });
    }
    for (const card of cards) {
      for (const action of card.actions) {
        const target = actionTarget(action);
        // A target with a connector → no line; the card draws a stub instead (see OutgoingStubs).
        if (connectors.has(target)) continue;
        // Dangling next_card_uuid (target card no longer exists) → silently no edge, no separate
        // "broken actions" inspector.
        if (target !== COMPLETION_CARD_ID && !cardUuids.has(target)) continue;
        const data: CardActionEdgeData = {
          actionType: action.actionType,
          answerValue: action.answerValue,
          sourceCardUuid: card.uuid,
          actionId: action.id,
        };
        result.push({
          id: `${card.uuid}:${action.id}`,
          source: card.uuid,
          sourceHandle: "out",
          target,
          targetHandle: "in",
          type: "cardAction",
          data: data as unknown as Record<string, unknown>,
          markerEnd: { type: MarkerType.ArrowClosed, color: colorForActionType(action.actionType) },
        });
      }
    }
    return result;
  }, [cards, connectors]);

  // One reusable create/edit modal — body is a function of `params` (sourceCardUuid, actionId,
  // initialNextCardUuid) so the same useModal() instance serves every connection-drag and every edge
  // click, rather than one useModal() per card/edge.
  const editActionModal = useModal({
    title: "Actie",
    body: (params?: string[]) => {
      if (!params) return null;
      const [sourceCardUuid, actionId, initialNextCardUuid] = params;
      const sourceCard = cards.find((c) => c.uuid === sourceCardUuid);
      const existingAction = sourceCard?.actions.find((a) => a.id === actionId);
      return (
        <EditCardActionModal
          cards={cards}
          updateCard={props.api.updateCard}
          sourceCardUuid={sourceCardUuid}
          existingAction={existingAction}
          initialNextCardUuid={initialNextCardUuid || null}
          onSaved={patchCard}
          getFreshCards={getFreshCards}
        />
      );
    },
  });

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onNodeDrag={onNodeDrag}
      onNodeDragStop={onNodeDragStop}
      nodeTypes={cardFlowNodeTypes}
      edgeTypes={cardFlowEdgeTypes}
      fitView
      // How close (px) a dragged connection has to be released to a card's handle to snap onto it —
      // the default (20) makes dropping on the target card's small handle needlessly precise.
      connectionRadius={50}
      onConnect={(connection) => {
        if (!connection.source || !connection.target) return;
        if (connection.source === connection.target) return;
        if (connection.source === OFFSET_STEPS_CARD_ID) {
          const card = cards.find((c) => c.uuid === connection.target);
          if (card) setFirstCard(card);
          return;
        }
        // No optimistic edge mutation before the modal opens — nodes/edges stay a pure derivation of
        // local `cards`, so an edge only appears once the action modal actually saves (via
        // EditCardActionModal's onSaved -> patchCard above). An empty next card (the completion
        // placeholder) saves the action without one.
        editActionModal.open([
          connection.source,
          "",
          connection.target === COMPLETION_CARD_ID ? "" : connection.target,
        ]);
      }}
      onEdgeClick={(_event, edge) => {
        if (edge.type !== "cardAction") return;
        const data = edge.data as unknown as CardActionEdgeData;
        editActionModal.open([data.sourceCardUuid, data.actionId, ""]);
      }}
    >
      <Background />
      <Controls />
    </ReactFlow>
  );
};
